import {
  useEffect,
  useState
} from 'react';
import './App.css';
import {
  Route,
  Routes
} from "react-router";
import { PageLayout } from "./components/pageLayout/pageLayout";
import { MainPage } from "./pages/mainPage/mainPage";
import i18n from "./i18n";
import { useGetAllStatusCodeQuery } from "./api/statusCodeService";
import { useGetAllMethodQuery } from "./api/methodService";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "./store";
import {
  hideErrorModal,
  setLogin,
  setMethods,
  setProjectName,
  setStatusCode
} from "./store/slices/mainSlice";
import { SaveModal } from "./components/saveModal/saveModal";
import { useTranslation } from "react-i18next";
import { Spinner } from "./components/spinner/spinner";
import { useSearchParams } from "react-router-dom";
import {
  logout,
  setIsSandbox,
  setToken
} from "./store/slices/authSlice";
import logoutIcon from "./shared/images/logout.svg";
import { MessageWindow } from "./shared/components/messageWindow/messageWindow";

const stage = process.env.REACT_APP_STAGE;

export const App = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch<AppDispatch>();

  const projectName = useSelector((state: RootState) => state.mainSlice.projectName);
  const showSpinner = useSelector((state: RootState) => state.mainSlice.showSpinner);
  const errorModal = useSelector((state: RootState) => state.mainSlice.errorModal);
  const { token, isSandbox } = useSelector((state: RootState) => state.authSlice);

  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isClickLogout, setIsClickLogout] = useState<boolean>(false);

  const { data: methods, isSuccess: isSuccessLoadMethods } = useGetAllMethodQuery("", { skip: isLoading });
  const { data: statusCodes, isSuccess: isSuccessLoadStatutes } = useGetAllStatusCodeQuery("", { skip: isLoading });

  useEffect(() => {
    i18n.changeLanguage("en");
  }, []);

  useEffect(() => {
    const handleResize = () => {
      document.documentElement.style.setProperty('--100vh', `${window.innerHeight}px`);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!!token) {
      dispatch(setToken(token));
    } else {
      setIsLoading(false);
    }

    const isSandbox = searchParams.get("isSandbox");
    if (!!isSandbox) {
      dispatch(setIsSandbox(!!+isSandbox));
    }

    const login = searchParams.get("login");
    if (!!login) {
      dispatch(setLogin(decodeURIComponent(login)));
    }

    const projectName = searchParams.get("projectName");
    if (!!projectName) {
      dispatch(setProjectName(decodeURIComponent(projectName)));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!!projectName) {
      document.title = `${t("common.tht")} ${projectName}`;
    } else {
      document.title = t("common.tht");
    }
    if (!!token && !!projectName && isSandbox != null) {
      setSearchParams({});
      setIsLoading(false);
    }
  }, [token, projectName, isSandbox]);

  useEffect(() => {
    if (isSuccessLoadMethods && !!methods) {
      dispatch(setMethods(methods));
    }
  }, [isSuccessLoadMethods]);

  useEffect(() => {
    if (isSuccessLoadStatutes && !!statusCodes) {
      dispatch(setStatusCode(statusCodes));
    }
  }, [isSuccessLoadStatutes]);

  useEffect(() => {
    if (isClickLogout && !token && isSandbox == null) {
      const loginUrl = process.env.REACT_APP_LOGIN_URL as string;
      window.location.href = `${loginUrl}/#/logout`;
    }
  }, [isClickLogout, token, isSandbox]);

  const onClickLogout = (): void => {
    dispatch(logout());
    setIsClickLogout(true);
  }

  const onCloseForbiddenModal = (): void => {
    dispatch(hideErrorModal());
  }

  return (
    <div className="container">
      { showSpinner && (<Spinner />) }
      <div className={`mainHeader ${stage}`}>
        <span>
          {stage === "dev" && t("common.dev")}
          {stage === "qa" && t("common.qa")}
          {stage === "stage" && t("common.stage")}
          {stage === "prod" && t("common.defaultTitle")}
        </span>
        <div className="appTitle">
          <span>{projectName}</span>
          <div className="action"
               title={t("common.logout")}
               onClick={onClickLogout}>
            <img src={logoutIcon} alt=""/>
          </div>
        </div>
      </div>
      <SaveModal/>
      {errorModal && (
        <MessageWindow onClose={onCloseForbiddenModal}
                       title={errorModal.title}
                       description={errorModal.description}
                       primaryText={errorModal.primaryText}/>
      )}
      {!isLoading && (
        <Routes>
          <Route element={<PageLayout />}>
            <Route path="/" element={<MainPage />}/>
          </Route>
        </Routes>
      )}
    </div>
  );
}

export default App;
